import { ChecklistItemComponent } from "../Report/ChecklistItemComponent";
import { competencyIcons } from "../Report/CompetencyIcons";
import { checklistPageConfig, type CompetencyChecklistData } from "../Data/checklistData";

interface ChecklistPageProps {
  checklists: [string, CompetencyChecklistData][];

  reportData?: any;
}

const accentBorderColors: Record<string, string> = {
  cyan: "hsl(190,95%,55%)",
  teal: "hsl(175,80%,42%)",
  purple: "hsl(260,60%,55%)",
  gold: "hsl(300,70%,55%)",
};

const accentDividerColors: Record<string, string> = {
  cyan: "hsla(190,95%,55%,0.2)",
  teal: "hsla(175,80%,42%,0.2)",
  purple: "hsla(260,60%,55%,0.2)",
  gold: "hsla(300,70%,55%,0.2)",
};

const containerGlowStyles: Record<string, string> = {
  cyan: "ring-1 ring-cyan-500/15 shadow-[0_0_30px_-10px_hsla(190,95%,55%,0.25)]",
  teal: "ring-1 ring-teal-500/15 shadow-[0_0_30px_-10px_hsla(172,66%,50%,0.25)]",
  purple: "ring-1 ring-purple-500/15 shadow-[0_0_30px_-10px_hsla(260,60%,55%,0.25)]",
  gold: "ring-1 ring-purple-400/15 shadow-[0_0_30px_-10px_hsla(300,70%,55%,0.25)]",
};

const cardGradients: Record<string, string> = {
  cyan: "from-cyan/15 via-transparent to-transparent",
  teal: "from-teal/15 via-transparent to-transparent",
  purple: "from-purple/15 via-transparent to-transparent",
  gold: "from-purple/15 via-transparent to-transparent",
};

const iconGlowStyles: Record<string, string> = {
  cyan: "shadow-[inset_0_0_12px_hsla(190,95%,55%,0.15)]",
  teal: "shadow-[inset_0_0_12px_hsla(172,66%,50%,0.15)]",
  purple: "shadow-[inset_0_0_12px_hsla(260,60%,55%,0.15)]",
  gold: "shadow-[inset_0_0_12px_hsla(300,70%,55%,0.15)]",
};

const competencyAccents: Record<string, string> = {
  "Goal Setting": "cyan",
  Delegation: "teal",
  "Giving Feedback": "purple",
  "Conflict Management": "gold",
};

const contextualHeaders: Record<string, string> = {
  "Goal Setting": "When setting goals:",
  Delegation: "When delegating:",
  "Giving Feedback": "When giving feedback:",
  "Conflict Management": "When navigating conflict:",
};

export function ChecklistPage({ checklists, reportData }: ChecklistPageProps) {




   const visibleChecklists = checklists.filter((item, index) => {
    const questNumber = index + 1;

    const interactions =
      reportData?.questApproachReport?.[questNumber]?.interactions || [];

    return interactions.length > 0;
  });


  return (
    <div className="report-page checklist-bg p-10 md:p-16 overflow-hidden h-full flex flex-col">

      {/* ---------------- HEADER + BG (UNCHANGED) ---------------- */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-30" />
      </div>

      <div className="relative z-10 h-full flex flex-col">

        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-base tracking-[0.3em] uppercase text-teal mb-3">
            {checklistPageConfig.sectionLabel}
          </p>

          <h2 className="text-5xl font-light tracking-wide">
            <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              {checklistPageConfig.title}
            </span>
          </h2>

          <p className="text-muted-foreground mt-3 mx-auto text-base">
            {checklistPageConfig.subtitle}
          </p>
            <div className="w-24 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-teal/60 to-transparent"></div>
        </div>

    

        <div
  className={`flex-1 grid grid-cols-1 ${
    visibleChecklists.length > 1 ? "md:grid-cols-2" : ""
  } gap-5`}
>

         {visibleChecklists.map(([competency, data], index) => { // ## CHANGE add index

  const accent = competencyAccents[competency] || "cyan";
  const IconComponent = competencyIcons[competency];

  /* =====================================================
     ## CHANGE 2 – Get correct quest number
  ===================================================== */

  const originalIndex = checklists.findIndex(
    ([name]) => name === competency
  );

  const questNumber = originalIndex + 1;

  const questInteractions =
    reportData?.questApproachReport?.[questNumber]?.interactions || [];



  const topItems =
  questInteractions.length > 0
    ? questInteractions
        // ## CHANGE – limit to top 3
        .slice(0, 3)
        .map((interaction: any) => ({
          id: `${questNumber}-${interaction.interactionId}`,
          text: interaction.checklist,
          priority: interaction.priorityTag,
          checked: interaction.result,
        }))
    : [];

            return (
              <div
      key={competency}
      className={`relative p-5 rounded-2xl bg-card/60 border border-border backdrop-blur-sm flex flex-col overflow-hidden 
      ${containerGlowStyles[accent]}
      
      ${
        visibleChecklists.length % 2 !== 0 &&
        index === visibleChecklists.length - 1
          ? "md:col-span-2"
          : ""
      } 
      `} // ## CHANGE last box full width
      style={{
        borderLeft: `2px solid ${accentBorderColors[accent]}`
      }}
    >

                {/* Header */}
                <div className="relative flex items-center gap-2 mb-3">
                  <div
                    className={`w-8 h-8 rounded-xl bg-gradient-to-br from-slate-900 to-black flex items-center justify-center border border-primary/20 ${iconGlowStyles[accent]}`}
                  >
                    {IconComponent ? <IconComponent /> : data.icon}
                  </div>

                  <h3 className="text-lg font-medium text-foreground">
                    {contextualHeaders[competency] || competency}
                  </h3>
                </div>

                {/* Divider */}
                <div
                  className="relative h-px mb-2"
                  style={{
                    background: `linear-gradient(to right, transparent, ${accentDividerColors[accent]}, transparent)`
                  }}
                />

                {/* Checklist Items */}
                <div className="relative flex flex-col gap-3 pl-2">
                 
                  {topItems.length > 0 && (
  <div className="relative flex flex-col gap-3 pl-2">
    {topItems.map((item: any) => (
      <ChecklistItemComponent
        key={item.id}
        item={item}
        accent={accent}
      />
    ))}
  </div>
)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
